/*
 *  This file hrone-confirm-dialog.component.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2022
 *  (C) 2015-2022. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */

import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, computed, input } from '@angular/core';
import { HroneDialogComponent, THroneDialogSize } from 'src/app/hrone-ui-library/hrone-dialog/hrone-dialog.component';
import { NgClass, SlicePipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { mandatoryField } from '../../validators/validators';
import { customValidations } from '../../validators/validations.enum';
import { MatInputModule } from '@angular/material/input';
import { SharedService } from '../../services/shared.service';
import { SendBackEmployeeList } from 'src/app/modules/inbox/modules/inbox-header2/inbox-header2.component';
import { MatRadioModule } from '@angular/material/radio';
import { HroneAvatarComponent } from 'src/app/hrone-ui-library/hrone-avatar/hrone-avatar.component';
import { HroneTooltipDirective } from 'src/app/hrone-ui-library/hrone-tooltip/hrone-tooltip.directive';

/** Confirm dialog width: small 400px, medium 480px, large 520px (default). */
export type THroneConfirmDialogSize = 'small' | 'medium' | 'large';

/** small / medium map onto hrone-dialog presets; large (520px) has no preset, so it is set inline. */
const CONFIRM_DIALOG_SIZE_MAP: Record<THroneConfirmDialogSize, { size: THroneDialogSize; style: Record<string, string> }> = {
  small: { size: 'x-small', style: {} },
  medium: { size: 'small', style: {} },
  large: { size: 'medium', style: { width: '520px' } },
};

@Component({
  selector: 'app-hrone-confirm-dialog',
  templateUrl: './hrone-confirm-dialog.component.html',
  styleUrls: ['./hrone-confirm-dialog.component.scss'],
  imports: [HroneDialogComponent, MatInputModule, ReactiveFormsModule, NgClass, MatRadioModule, HroneAvatarComponent, SlicePipe, FormsModule, HroneTooltipDirective]
})
export class HroneConfirmDialogComponent implements OnChanges {
  @Input() confirmDialog = {
    show: false
  };
  @Input() cross = false;
  @Output() crossEvent: EventEmitter<boolean> = new EventEmitter();
  @Input() header = 'Are you sure you want to inactivate?';
  @Input() subHeader = '';
  @Input() message = 'You will not be able to activate this again. This action can not be undone';
  @Input() confirmLabel = 'YES, INACTIVATE';
  @Input() cancelLabel = 'CANCEL';
  @Output() confirm = new EventEmitter();
  @Output() cancel = new EventEmitter();
  @Input() note = '';
  @Input() disableButton = false;
  @Input() isCancelButtonRequired = true;
  @Input() isRemarksRequired = false;
  @Input() isWarning = false;
  @Input() isInfo = false;
  @Input() formControlLabel = "Specify the reason";
  @Input() remarksPlaceholder = '';
  sendBackMessage: Array<string> = [];
  @Input() maxlength = 500;

  remarks = new FormControl('');
  @Input() isBtnDanger = false;
  @Input() warningHeader = 'Warning';
  @Input() infoHeader = 'Info';
  @Input() sendBackEmployeeArray : SendBackEmployeeList[] | undefined;
  selectedLevel = 0;
  showSendBackToolTip: { [key: number]: boolean } = {};
  @Input() customClass : string = '';
  @Input() isSendBackEmployee : boolean = false;

  /** Dialog width: small 400px, medium 480px, large 520px (default). */
  readonly size = input<THroneConfirmDialogSize>('large');
  protected readonly dialogSize = computed(() => CONFIRM_DIALOG_SIZE_MAP[this.size()].size);
  protected readonly dialogStyle = computed(() => CONFIRM_DIALOG_SIZE_MAP[this.size()].style);

  constructor(
    public _shared: SharedService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.isRemarksRequired?.currentValue) this.remarks.setValidators([Validators.required, mandatoryField, Validators.pattern(customValidations.allowedCharacters)])
    this.remarks.updateValueAndValidity();
    if (this.isRemarksRequired && this.message) {
      this.sendBackMessage = this.message.split(',');
    }
    if(this.sendBackEmployeeArray?.length){
      const levelArr = this.sendBackEmployeeArray.map(ele=>ele.levelNo);
      this.selectedLevel = levelArr[levelArr.length-1]
    }
  }

  confirmClicked() {
    this.confirmDialog.show = false;
    const sendBackObj = {
      remarks: this.isRemarksRequired ? this.remarks : '',
      levelNo: this.selectedLevel
    }
    this.confirm.emit(sendBackObj);
    if (this.isRemarksRequired) this.resetErrorState();
  }
  crossClicked() {
    if (this.isRemarksRequired) this.resetErrorState();
    this.confirmDialog.show = false;
    this.selectedLevel = 0;
    this.crossEvent.emit();
  }

  cancelClicked() {
    if (this.isRemarksRequired) this.resetErrorState();
    this.cancel.emit();
    this.confirmDialog.show = false;
  }

  resetErrorState(): void {
    this.remarks.reset({ value: '', disabled: false });
    this.remarks.setValidators([Validators.required, mandatoryField, Validators.pattern(customValidations.allowedCharacters)]);
    this.remarks.updateValueAndValidity();
  }
}
